import { create } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';

import {
  NotificationContentCategory,
  NotificationType,
} from '@/features/notifications/notification-types';
import { OrgRole } from '@/features/org/validation/org-role.schema';
import { ProjectHubEntryFilterType } from '@/features/projects/projects-hub/components/project-hub-filter/project-hub-entry-filter-type';
import { ProjectRole } from '@/features/projects/shared/types/project-role';
import { ProjectStatus } from '@/features/projects/shared/types/project-status';
import { DocumentVisibility } from '@/shared/components/ui/upload/document-upload-dialog';
import { InvitationStatus } from '@/shared/types/api';
import { NotificationReadStatus } from '@/shared/types/filter/notification-filter-type';
import { getNonEmptyArrayNoDuplicates } from '@/shared/utils/set-utils';

export interface HubFilterState {
  statuses?: ProjectStatus[];
  partnerIds?: string[];
  roles?: ProjectRole[];
  notificationStatuses?: NotificationReadStatus[];
  lastEntry?: ProjectHubEntryFilterType[];
  searchQuery: string;
  fromDate?: Date;
  toDate?: Date;
  visibility?: DocumentVisibility[];
  projectIds?: string[];
  subprojectIds?: string[];
  uploadedBy?: string[];
  invitationStatus?: InvitationStatus[];
  userRoles?: OrgRole[];
  notificationTypes?: NotificationType[];
  notificationCategories?: NotificationContentCategory[];
  setFromDate: (fromDate?: Date) => void;
  setToDate: (toDate?: Date) => void;
  setStatuses: (statuses?: ProjectStatus[]) => void;
  setPartnerIds: (partnerIds?: string[]) => void;
  setRoles: (roles?: ProjectRole[]) => void;
  setNotificationStatuses: (notifications?: NotificationReadStatus[]) => void;
  setLastEntry: (lastEntry?: ProjectHubEntryFilterType[]) => void;
  setVisibility: (visibility?: DocumentVisibility[]) => void;
  setProjectIds: (projectIds?: string[]) => void;
  setSubprojectIds: (subprojectIds?: string[]) => void;
  setUploadedBy: (uploadedBy?: string[]) => void;
  setInvitationStatus: (invitationStatus?: InvitationStatus[]) => void;
  setUserRoles: (userRoles?: OrgRole[]) => void;
  setSearchQuery: (query: string) => void;
  setNotificationTypes: (notificationTypes?: NotificationType[]) => void;
  setNotificationCategories: (
    notificationCategories?: NotificationContentCategory[],
  ) => void;
  reset: () => void;
}

export const createHubFilterStore = (
  persistOptions?: Omit<
    PersistOptions<HubFilterState, Partial<HubFilterState>>,
    'name'
  > & { name: string },
) => {
  const storeConfig = (set: any) => ({
    searchQuery: '',
    fromDate: undefined,
    toDate: undefined,
    visibility: undefined,
    projectIds: undefined,
    subprojectIds: undefined,
    uploadedBy: undefined,
    invitationStatus: undefined,
    userRoles: undefined,
    setFromDate: (fromDate: Date | undefined) => set({ fromDate }),
    setToDate: (toDate: Date | undefined) => set({ toDate }),
    setStatuses: (statuses: ProjectStatus[] | undefined) =>
      set({
        statuses: getNonEmptyArrayNoDuplicates(statuses),
      }),
    setPartnerIds: (partnerIds: string[] | undefined) =>
      set({
        partnerIds: getNonEmptyArrayNoDuplicates(partnerIds),
      }),
    setRoles: (roles: ProjectRole[] | undefined) =>
      set({
        roles: getNonEmptyArrayNoDuplicates(roles),
      }),
    setNotificationStatuses: (
      notifications: NotificationReadStatus[] | undefined,
    ) =>
      set({
        notificationStatuses: getNonEmptyArrayNoDuplicates(notifications),
      }),
    setLastEntry: (lastEntry: ProjectHubEntryFilterType[] | undefined) =>
      set({
        lastEntry: getNonEmptyArrayNoDuplicates(lastEntry),
      }),
    setVisibility: (visibility: DocumentVisibility[] | undefined) =>
      set({ visibility: getNonEmptyArrayNoDuplicates(visibility) }),
    setProjectIds: (projectIds: string[] | undefined) =>
      set({ projectIds: getNonEmptyArrayNoDuplicates(projectIds) }),
    setSubprojectIds: (subprojectIds: string[] | undefined) =>
      set({ subprojectIds: getNonEmptyArrayNoDuplicates(subprojectIds) }),
    setUploadedBy: (uploadedBy: string[] | undefined) =>
      set({ uploadedBy: getNonEmptyArrayNoDuplicates(uploadedBy) }),
    setInvitationStatus: (invitationStatus: InvitationStatus[] | undefined) =>
      set({
        invitationStatus: getNonEmptyArrayNoDuplicates(invitationStatus),
      }),
    setUserRoles: (userRoles: OrgRole[] | undefined) =>
      set({
        userRoles: getNonEmptyArrayNoDuplicates(userRoles),
      }),
    setSearchQuery: (searchQuery: string) => set({ searchQuery }),
    setNotificationTypes: (notificationTypes: NotificationType[] | undefined) =>
      set({
        notificationTypes: getNonEmptyArrayNoDuplicates(notificationTypes),
      }),
    setNotificationCategories: (
      notificationCategories: NotificationContentCategory[] | undefined,
    ) =>
      set({
        notificationCategories: getNonEmptyArrayNoDuplicates(
          notificationCategories,
        ),
      }),
    reset: () =>
      set({
        statuses: undefined,
        partnerIds: undefined,
        roles: undefined,
        lastEntry: undefined,
        searchQuery: '',
        fromDate: undefined,
        toDate: undefined,
        visibility: undefined,
        projectIds: undefined,
        subprojectIds: undefined,
        uploadedBy: undefined,
        invitationStatus: undefined,
        userRoles: undefined,
        notificationStatuses: undefined,
        notificationTypes: undefined,
        notificationCategories: undefined,
      }),
  });

  if (persistOptions) {
    return create<HubFilterState>()(persist(storeConfig, persistOptions));
  }

  return create<HubFilterState>()(storeConfig);
};

export const useGenericActiveProjectHubFilterCount = ({
  statuses,
  partnerIds,
  roles,
  notificationStatuses,
  lastEntry,
  fromDate,
  toDate,
  visibility,
  projectIds,
  subprojectIds,
  uploadedBy,
  invitationStatus,
  userRoles,
  notificationTypes,
  notificationCategories,
}: HubFilterState) =>
  Object.values({
    statuses,
    partnerIds: partnerIds,
    roles,
    notificationStatuses,
    lastEntry,
    // Count fromDate + toDate as a single "date range" filter category
    dateRange: fromDate || toDate,
    visibility,
    projectIds,
    subprojectIds,
    uploadedBy,
    invitationStatus,
    userRoles,
    notificationTypes,
    notificationCategories,
  }).filter(Boolean).length;

export const useGenericHasProjectHubFilters = (props: HubFilterState) =>
  useGenericActiveProjectHubFilterCount(props) > 0 ||
  props.searchQuery.length > 0;

export const useProjectHubFilterStore = createHubFilterStore();
export const useActiveProjectHubFilterCount = () =>
  useGenericActiveProjectHubFilterCount(useProjectHubFilterStore());
export const useHasProjectHubFilters = () =>
  useGenericHasProjectHubFilters(useProjectHubFilterStore());
